import { type Dispatch, type SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import {
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  type TextInput,
} from "react-native";
import { setDraftVoucher } from "../../store/draftReservation";

import {
  type StoredDiscountFragment,
  isWeb,
  useGetVirtualWalletBalanceQuery,
  useListOwnedCouponsQuery,
} from "@stepapp/core";
import {
  Block,
  BottomSheet,
  Button,
  Divider,
  Input,
  Loader,
  Modal,
  Snackbar,
  SvgIcon,
  Touchable,
  Typography,
  palette,
  spacing,
  useHeaderHeight,
} from "@stepapp/ui";

import { useTranslation } from "react-i18next";
import { focusInput } from "../../helpers";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import CouponItem from "../CouponItem";
import StepappWallet from "../StepappWallet";

type Props = {
  isVisible: boolean;
  setIsVisible: Dispatch<SetStateAction<boolean>>;
  setCoupon?: (couponName: string) => void;
  errorSnackbarVisible: boolean;
  successSnackbarVisible: boolean;
  selectedCoupon?: string;
  setErrorSnackbarVisible: Dispatch<SetStateAction<boolean>>;
  setSuccessSnackbarVisible: Dispatch<SetStateAction<boolean>>;
  loadingCoupon: boolean;
  redeemCoupon: (code: string) => Promise<string | false | undefined>;
  screen?: "serviceSummary" | "payment";
  description: string;
  title: string;
};

type SnackbarRef = {
  hide: () => void;
} | null;

const CouponModal = ({
  isVisible,
  setIsVisible,
  selectedCoupon,
  errorSnackbarVisible,
  successSnackbarVisible,
  setErrorSnackbarVisible,
  setSuccessSnackbarVisible,
  redeemCoupon,
  setCoupon,
  loadingCoupon,
  screen = "serviceSummary",
  description,
  title,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: skip
}: Props) => {
  const { t } = useTranslation();
  const isLogged = useAppSelector(({ user }) => !!user?.id);
  const inputRef = useRef<TextInput>(null);
  const snackbarRef = useRef<SnackbarRef>(null);
  const headerHeight = useHeaderHeight();
  const [code, setCode] = useState("");
  const appDispatch = useAppDispatch();

  const { data: walletBalance } = useGetVirtualWalletBalanceQuery({ skip: !isLogged });
  const { data, loading, refetch } = useListOwnedCouponsQuery({ skip: !isLogged });

  const isServiceSummaryScreen = screen === "serviceSummary";

  const vouchers = useMemo(
    () => (data?.listOwnedCoupons?.filter(Boolean) || []) as StoredDiscountFragment[],
    [data],
  );
  const isDisabled = code?.length === 0;

  const hideSnackbar = useCallback(() => {
    if (errorSnackbarVisible) {
      snackbarRef?.current?.hide();
    }
  }, [errorSnackbarVisible]);

  const toggleBottomSheet = useCallback(() => {
    setIsVisible((v) => !v);
  }, [setIsVisible]);

  const handleCouponPress = useCallback(
    (coupon: StoredDiscountFragment | null) => {
      setCoupon?.(coupon?.code || "");
      appDispatch(setDraftVoucher(coupon?.code || ""));
    },
    [appDispatch, setCoupon],
  );

  const handleOnAddPress = useCallback(() => {
    focusInput(inputRef);
    hideSnackbar();
  }, [hideSnackbar]);

  const closeCouponModal = useCallback(() => {
    setSuccessSnackbarVisible(false);
    setErrorSnackbarVisible(false);
  }, [setSuccessSnackbarVisible, setErrorSnackbarVisible]);

  const handleRedeemCoupon = useCallback(async () => {
    if (!code) return;

    Keyboard.dismiss();
    setCode("");
    const response = await redeemCoupon(code);
    if (response) {
      setCoupon?.(response);
      await refetch();
    }
  }, [code, redeemCoupon, refetch, setCoupon]);

  return (
    <BottomSheet
      contentStyle={
        isServiceSummaryScreen ? styles.containerServiceSummary : styles.containerPayment
      }
      enableScroll={false}
      toggle={toggleBottomSheet}
      visible={isVisible}
    >
      <Block
        row
        paddingBottom={isServiceSummaryScreen ? 3 : 2}
        paddingHorizontal={2.5}
        paddingTop={3}
      >
        <Touchable
          onPress={() => {
            setIsVisible(false);
            if (errorSnackbarVisible) {
              setErrorSnackbarVisible(false);
            }
          }}
        >
          <Block alignCenter justifyCenter style={styles.closeIcon}>
            <SvgIcon color={palette.font.placeholder} name="cross" size={36} />
          </Block>
        </Touchable>

        {isServiceSummaryScreen && (
          <Typography style={styles.title} variant="newH2">
            {t("screens.coupons.title")}
          </Typography>
        )}
      </Block>

      {isServiceSummaryScreen && <Divider spaceless color={palette.secondary.grey} />}

      <ScrollView
        keyboardShouldPersistTaps="always"
        style={styles.containerCoupon}
        onTouchStart={hideSnackbar}
      >
        {isServiceSummaryScreen ? (
          <Block style={styles.couponDescription}>
            <Typography variant="paragraph">{t("screens.coupons.description")}</Typography>
          </Block>
        ) : (
          <Block>
            <Typography variant="newH1">{t("screens.redeemCoupon.voucherTitle")}</Typography>
          </Block>
        )}

        <Block paddingVertical={2}>
          <Input
            onChangeText={(text) => {
              setCode(text);
              hideSnackbar();
            }}
            onFocus={hideSnackbar}
            placeholder={t("screens.coupons.typeCode")}
            ref={inputRef}
            showAnimatedLabel={false}
            value={code}
          />
          {isServiceSummaryScreen && (
            <Button
              disabled={isDisabled || loadingCoupon}
              label={t("screens.redeemCoupon.add")}
              loading={loadingCoupon}
              onPress={() => {
                handleRedeemCoupon();
              }}
              type="primary"
            />
          )}
        </Block>

        {isServiceSummaryScreen && (
          <>
            <Divider color={palette.secondary.grey} />

            <StepappWallet
              isHeadlineVisible
              onAddPress={() => handleOnAddPress()}
              balance={walletBalance?.getVirtualWalletBalance?.balance || 0}
            />

            <Block paddingVertical={2.5}>
              <Divider color={palette.secondary.grey} />
            </Block>

            <Block>
              <Block marginBottom={2}>
                <Typography variant="sectionTitle">{t("screens.coupons.yourDiscounts")}</Typography>
              </Block>

              {loading ? (
                <Loader />
              ) : vouchers?.length > 0 ? (
                vouchers.map((item) => (
                  <CouponItem
                    {...item}
                    pickMode
                    key={item.id}
                    onPress={handleCouponPress}
                    selectedCoupon={selectedCoupon}
                  />
                ))
              ) : (
                <Typography type="secondary" variant="paragraph">
                  {t("screens.coupons.emptyCoupons")}
                </Typography>
              )}
            </Block>
          </>
        )}
      </ScrollView>

      <Snackbar
        isVisible={!isWeb && errorSnackbarVisible}
        ref={snackbarRef}
        setIsVisible={setErrorSnackbarVisible}
        text={title}
        type="error"
      />

      <Modal
        description={description}
        primaryAction={closeCouponModal}
        primaryActionLabel={successSnackbarVisible ? t("common.OK") : t("common.tryAgain")}
        title={title}
        toggle={closeCouponModal}
        visible={isWeb && (successSnackbarVisible || errorSnackbarVisible)}
      />

      {!isServiceSummaryScreen && (
        <KeyboardAvoidingView
          behavior={Platform.select({ default: "padding", android: undefined })}
          keyboardVerticalOffset={headerHeight + 60}
        >
          <Block style={styles.containerPaymentCouponButton}>
            <Divider spaceless color={palette.secondary.grey} />
            <Button
              disabled={isDisabled}
              label={t("screens.redeemCoupon.add")}
              onPress={handleRedeemCoupon}
              style={styles.paymentCouponButton}
              type="primary"
            />
          </Block>
        </KeyboardAvoidingView>
      )}
    </BottomSheet>
  );
};

export default CouponModal;

const styles = StyleSheet.create({
  closeIcon: {
    backgroundColor: palette.common.black,
    borderRadius: 20,
  },
  containerCoupon: {
    paddingBottom: spacing.unit * 2,
    paddingHorizontal: spacing.unit * 2.5,
  },
  containerPayment: {
    height: spacing.deviceHeight * 0.8,
    justifyContent: "space-between",
  },
  containerPaymentCouponButton: {
    backgroundColor: palette.common.white,
  },
  containerServiceSummary: {
    height: spacing.deviceHeight * 0.9,
  },
  couponDescription: {
    paddingTop: 24,
  },
  paymentCouponButton: {
    marginBottom: 14,
    marginHorizontal: 20,
    marginTop: 8,
  },
  title: {
    marginLeft: 16,
  },
});
